import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "./pd-steps/pd-steps.js";
import { LitElement, css, html } from "lit";
import { property, state } from "lit/decorators.js";
import { localized, msg } from "@lit/localize";
import { pdIcons } from "@progressive-development/pd-icon";
import "@progressive-development/pd-icon/pd-icon";
import "@progressive-development/pd-forms/pd-button";
//#region src/pd-wizard/PdWizard.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
var PdWizard = class PdWizard extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.currentNumber = -99;
		this.wizardSteps = [];
		this.submitDisabled = false;
		this.withEarlySubmit = false;
		this._submitted = false;
	}
	static {
		this.styles = [css`
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;

        --_border-radius: var(--pd-wizard-border-radius, 0);
      }

      .wiz-header {
        position: relative;
        flex-shrink: 0;
        background: var(--pd-wizard-header-bg, var(--pd-default-col));
        color: var(--pd-wizard-header-color, var(--pd-on-primary-col));
        border-radius: var(--_border-radius) var(--_border-radius) 0 0;
      }

      .wiz-title {
        text-align: center;
        padding-top: var(--pd-spacing-md);
      }

      .title {
        font-family: var(--pd-default-font-title-family);
        font-size: var(--pd-wizard-header-font-size, 2em);
        font-weight: bolder;
      }

      /* The Close Icon */
      .close {
        position: absolute;
        top: 6px;
        right: 6px;
        cursor: pointer;
        --pd-icon-size: 2em;
        --pd-icon-col-active: var(
          --pd-wizard-close-icon-col,
          var(--pd-wizard-header-color, var(--pd-on-primary-col))
        );
      }

      .close:hover,
      .close:focus-visible {
        --pd-icon-col-active: var(
          --pd-wizard-close-icon-hover-col,
          var(--pd-default-hover-col)
        );
      }

      .close:focus-visible {
        outline: 2px solid var(--pd-focus-ring-col, currentColor);
        outline-offset: 2px;
        border-radius: var(--pd-radius-sm);
      }

      .wiz-content {
        flex-grow: 1;
        overflow-y: var(--pd-wizard-content-overflow, auto);
        background: var(--pd-wizard-content-bg, var(--pd-default-bg-col));
        padding: var(--pd-wizard-content-padding, 2rem 3.5rem 0.5rem 3.5rem);
      }

      .wiz-content-inner {
        max-width: var(
          --pd-wizard-content-max-width,
          var(--pd-content-max-width)
        );
        margin: 0 auto;
      }

      .wiz-buttons {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: var(--pd-wizard-buttons-justify, space-around);
        background: var(--pd-wizard-buttons-bg, var(--pd-default-light-col));
        padding: var(--pd-wizard-buttons-padding, var(--pd-spacing-md));

        border-radius: 0 0 var(--_border-radius) var(--_border-radius);
      }

      /* Next + Submit as one visual group */
      .wiz-actions {
        display: flex;
        align-items: center;
        gap: var(--pd-wizard-buttons-gap, var(--pd-spacing-md, 1rem));
      }

      /* Mobile: stack the buttons full-width (popup-dialog pattern),
         reversed so the primary action (Submit) ends up on top and
         Back/Cancel at the bottom. */
      @media (max-width: 600px) {
        .wiz-buttons {
          flex-direction: column-reverse;
          align-items: stretch;
          gap: var(--pd-spacing-sm, 0.5rem);
        }

        .wiz-actions {
          flex-direction: column-reverse;
          align-items: stretch;
          gap: var(--pd-spacing-sm, 0.5rem);
        }

        .wiz-buttons pd-button {
          width: 100%;
          --pd-button-width: 100%;
        }
      }

      @media (max-width: 768px) {
        .wiz-content {
          padding: var(--pd-wizard-content-padding, 2rem 2.5rem 0.5rem 2.5rem);
        }
      }

      @media (max-width: 440px) {
        .wiz-content {
          padding: var(--pd-wizard-content-padding, 1rem 1rem 0.5rem 1rem);
        }
        .close {
          --pd-icon-size: 1em;
        }
      }
    `];
	}
	render() {
		if (!this.wizardSteps.length) return html``;
		const currentStep = this.wizardSteps[this.currentNumber - 1];
		return html`
      <div class="wiz-header" part="header">
        <pd-icon
          class="close"
          activeIcon
          tabindex="0"
          role="button"
          aria-label="${msg("Wizard schließen", { id: "pd.wizard.close.aria" })}"
          icon="${pdIcons.ICON_XCLOSE}"
          @click="${this._closeWizard}"
          @keydown="${this._handleCloseKeydown}"
        ></pd-icon>

        <div class="wiz-title">
          <span class="title">${currentStep?.title ?? "No-Title"}</span>
        </div>

        <div class="wiz-breadcrumbs">
          <pd-steps
            .steps="${this.wizardSteps}"
            .currentStepNr="${this.currentNumber}"
          ></pd-steps>
        </div>
      </div>

      <div class="wiz-content" part="content">
        <div class="wiz-content-inner">
          ${this.wizardSteps.map((step, i) => html`
              <div
                style="${this.currentNumber === i + 1 ? "" : "display:none"}"
              >
                <slot name="step-${i + 1}"></slot>
              </div>
            `)}
        </div>
      </div>

      <div class="wiz-buttons" part="buttons">
        ${this.currentNumber >= 1 && this.wizardSteps.length ? this._renderButtons(currentStep) : ""}
      </div>
    `;
	}
	/**
	* Renders the navigation buttons: Cancel/Back on its own, Next and Submit
	* grouped in a shared actions container so they appear as a pair while
	* `--pd-wizard-buttons-justify` still controls the overall distribution.
	*/
	_renderButtons(currentStep) {
		const isLastStep = this.currentNumber === this.wizardSteps.length;
		const showNext = !isLastStep && currentStep?.next !== false;
		const showSubmit = isLastStep || this.withEarlySubmit;
		return html`
      ${this.currentNumber === 1 ? html`
              <pd-button
                @button-clicked="${this._closeWizard}"
                text="${msg("Abbrechen", { id: "pd.wizard.button.cancel" })}"
              ></pd-button>
            ` : html`
              <pd-button
                @button-clicked="${this._previousStep}"
                icon="${pdIcons.ICON_ARROW_BACK}"
                text="${msg("Zurück", { id: "pd.wizard.button.back" })}"
              ></pd-button>
            `}
      ${showNext || showSubmit ? html`
              <div class="wiz-actions">
                ${showNext ? html`
                        <pd-button
                          @button-clicked="${this._nextStep}"
                          icon="${pdIcons.ICON_ARROW_NEXT}"
                          text="${msg("Weiter", { id: "pd.wizard.button.next" })}"
                        ></pd-button>
                      ` : ""}
                ${showSubmit ? html`
                        <pd-button
                          icon="${pdIcons.ICON_SYNC}"
                          primary
                          ?disabled="${this._submitted || this.submitDisabled}"
                          @button-clicked="${this._submit}"
                          text="${msg("Absenden", { id: "pd.wizard.button.send" })}"
                        ></pd-button>
                      ` : ""}
              </div>
            ` : ""}
    `;
	}
	_previousStep() {
		this.dispatchEvent(new CustomEvent("previous-step"));
	}
	_nextStep() {
		this.dispatchEvent(new CustomEvent("next-step"));
	}
	_submit() {
		const detail = { submitted: false };
		this.dispatchEvent(new CustomEvent("submit-wizard", { detail }));
		this._submitted = detail.submitted;
	}
	_closeWizard() {
		this.dispatchEvent(new CustomEvent("close-wizard"));
	}
	/**
	* Handles keyboard events on the close icon.
	* Activates on Enter or Space key.
	*/
	_handleCloseKeydown(e) {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			this._closeWizard();
		}
	}
};
__decorate([property({ type: Number })], PdWizard.prototype, "currentNumber", void 0);
__decorate([property({ type: Array })], PdWizard.prototype, "wizardSteps", void 0);
__decorate([property({ type: Boolean })], PdWizard.prototype, "submitDisabled", void 0);
__decorate([property({ type: Boolean })], PdWizard.prototype, "withEarlySubmit", void 0);
__decorate([state()], PdWizard.prototype, "_submitted", void 0);
PdWizard = __decorate([localized()], PdWizard);
//#endregion
export { PdWizard };
